import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';

import AppShell from './AppShell';
import { socket } from '../socket';

const Wrap = styled.div`
  padding: 16px;
  padding-left: calc(16px + var(--safe-left));
  padding-right: calc(16px + var(--safe-right));
`;

const Card = styled.div`
  background: var(--tg-surface);
  border: 1px solid var(--tg-border);
  border-radius: var(--tg-radius);
  box-shadow: var(--tg-shadow);
  overflow: hidden;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 14px;
  border-bottom: 1px solid var(--tg-border);
  cursor: pointer;
  transition: background-color 0.2s ease-in-out;

  &:hover {
    background-color: rgba(255, 255, 255, 0.05);
  }

  &:last-child {
    border-bottom: none;
  }
`;

const Name = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: var(--tg-text);
  display: flex;
  align-items: center;
  gap: 8px;
`;

const OnlineIndicator = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: #25d366; // Green color for online status
`;

const Meta = styled.div`
  font-size: 12px;
  color: var(--tg-muted);

`;

const Users = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [error, setError] = useState('');
  const [onlineUsers, setOnlineUsers] = useState([]);

  const me = useMemo(() => {
    const token = localStorage.getItem('token');
    if (!token) return { username: '' };
    try {
      const decoded = jwtDecode(token);
      return { username: decoded?.username || '' };
    } catch {
      return { username: '' };
    }
  }, []);

  useEffect(() => {
    // 1. Fetch all users initially
    const fetchUsers = async () => {
      const token = localStorage.getItem('token');
      if (!token) return;

      const serverUrl = process.env.REACT_APP_SERVER_URL || 'https://connect-server-nfxv.onrender.com';
      try {
        const res = await axios.get(`${serverUrl}/api/users`, { headers: { Authorization: `Bearer ${token}` } });
        const allUsers = Array.isArray(res.data) ? res.data : [];
        
        // Filter out the current user from the list to display
        setUsers(allUsers.filter(u => u.username && u.username !== me.username));
        
        // Cache the full user data (including self) for other components
        localStorage.setItem('users_data', JSON.stringify(allUsers));
      } catch (error) {
        console.error('Failed to fetch users', error);
        setError('خطا در دریافت کاربران');
      }
    };

    fetchUsers();

    // 2. Set up WebSocket listener for real-time updates
    const handleOnlineUsersUpdate = (onlineUserList) => {
      setOnlineUsers(onlineUserList);
    };
    socket.on('update-online-users', handleOnlineUsersUpdate);

    // 3. Request the current list of online users when component mounts
    socket.emit('get-online-users');

    // 4. Cleanup on component unmount
    return () => {
      socket.off('update-online-users', handleOnlineUsersUpdate);
    };
  }, [me.username]);

  return (
    <AppShell title="کاربران">
      <Wrap>
        {error ? <Meta>{error}</Meta> : null}
        <Card>
          {users.length === 0 ? (
            <Row>
              <Meta>کاربری برای نمایش وجود ندارد.</Meta>
            </Row>
          ) : (
            users.map((u) => (
              <Row
                key={u.id || u.username}
                role="button"
                tabIndex={0}
                onClick={() => navigate(`/chat/${encodeURIComponent(u.username)}`)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    navigate(`/chat/${encodeURIComponent(u.username)}`);
                  }
                }}
              >
                <div>
                  <Name>
                    {onlineUsers.includes(u.username) && <OnlineIndicator />}
                    {u.username}
                  </Name>
                  <Meta>Start a chat</Meta>
                </div>
              </Row>
            ))
          )}
        </Card>
      </Wrap>
    </AppShell>
  );
};

export default Users;
